import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { cityAPI } from '../../services/api';
import { saveAs } from 'file-saver';
import SuccessModal from '../Common/SuccessModal';
import ConfirmModal from '../Common/ConfirmModal';
import ErrorModal from '../Common/ErrorModal';
import {
  AdminPage,
  PageToolbar,
  ExportMenu,
  CardGrid,
  EntityCard,
  Pagination,
  useRecordActions,
} from '../Common/admin';

const PAGE_SIZE = 12;
const NS = 'city.list';

const CityList = () => {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmData, setConfirmData] = useState(null);
  const [parentOptions, setParentOptions] = useState([]);
  const fileInputRef = React.useRef(null);

  const loadItems = useCallback(async () => {
    try {
      setLoading(true);
      const response = await cityAPI.getAll(appliedSearch, page, PAGE_SIZE);
      setItems(response.data.items || []);
      setTotalPages(response.data.totalPages || 1);
      setTotalCount(response.data.totalCount ?? 0);
      setSelectedIds([]);
    } catch (err) {
      setErrorMessage(t(`${NS}.messages.loadError`));
      setShowErrorModal(true);
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [appliedSearch, page, t]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  // Options for the parent dropdown shown inside the dialog.
  useEffect(() => {
    cityAPI
      .getAreas()
      .then((res) =>
        setParentOptions(
          (res.data || []).map((o) => ({ value: o.id, label: o.areaNameEng }))
        )
      )
      .catch((err) => console.error('Failed to load options:', err));
  }, []);

  // --- dialog-driven add / edit / details / delete ----------------------
  const formFields = useMemo(
    () => [
      {
        name: 'cityCode',
        label: t('city.create.fields.cityCode'),
        placeholder: t('city.create.fields.cityCode'),
        icon: 'fa-hashtag',
        type: 'text',
        required: true,
      },
      {
        name: 'cityNameAr',
        label: t('city.create.fields.nameAr'),
        placeholder: t('city.create.fields.nameAr'),
        icon: 'fa-language',
        type: 'text',
        required: true,
      },
      {
        name: 'cityNameEng',
        label: t('city.create.fields.nameEng'),
        placeholder: t('city.create.fields.nameEng'),
        icon: 'fa-globe',
        type: 'text',
        required: true,
      },
      {
        name: 'areaId',
        label: t('city.create.fields.area'),
        placeholder: t('city.create.fields.area'),
        icon: 'fa-map',
        type: 'select',
        required: true,
        options: parentOptions,
      },
      {
        name: 'isActive',
        label: t('city.create.fields.isActive'),
        placeholder: t('city.create.fields.isActive'),
        icon: 'fa-circle-check',
        type: 'checkbox',
        required: false,
      },
    ],
    [t, parentOptions]
  );

  const toDetails = useCallback(
    (record) => [
      { label: t('city.view.fields.cityCode'), value: record.cityCode },
      { label: t('city.view.fields.nameAr'), value: record.cityNameAr },
      { label: t('city.view.fields.nameEng'), value: record.cityNameEng },
      { label: t('city.view.fields.isActive'), value: Boolean(record.isActive) },
      { label: t('city.view.fields.area'), value: record.area?.areaNameEng },
    ],
    [t]
  );

  const actions = useRecordActions({
    api: cityAPI,
    fields: formFields,
    labels: {
      create: t('city.create.title'),
      edit: t('city.edit.title'),
      details: t('city.view.title'),
      deleteConfirm: t(`${NS}.messages.deleteConfirm`),
    },
    toDetails,
    onChanged: loadItems,
    onError: (message) => {
      setErrorMessage(message);
      setShowErrorModal(true);
    },
  });

  const handleSearch = () => {
    setPage(1);
    setAppliedSearch(searchTerm);
  };

  const handleBulkDelete = () => {
    if (selectedIds.length === 0) {
      setErrorMessage(t(`${NS}.messages.selectToDelete`));
      setShowErrorModal(true);
      return;
    }

    setConfirmAction(() => async () => {
      try {
        const response = await cityAPI.bulkDelete(selectedIds);
        setSuccessMessage(
          response.data.message ||
            t(`${NS}.messages.bulkDeleteSuccess`, { count: selectedIds.length })
        );
        setShowSuccessModal(true);
        setSelectedIds([]);
        loadItems();
      } catch (err) {
        setErrorMessage(
          err.response?.data?.message || t(`${NS}.messages.bulkDeleteError`)
        );
        setShowErrorModal(true);
      }
    });
    setConfirmData({
      title: t('common.confirm'),
      message: t(`${NS}.messages.bulkDeleteConfirm`, { count: selectedIds.length }),
      confirmText: t('common.delete'),
      cancelText: t('common.cancel'),
    });
    setShowConfirmModal(true);
  };

  const handleConfirm = async () => {
    setShowConfirmModal(false);
    if (confirmAction) await confirmAction();
    setConfirmAction(null);
    setConfirmData(null);
  };

  const handleCancel = () => {
    setShowConfirmModal(false);
    setConfirmAction(null);
    setConfirmData(null);
  };

  const handleSelectAll = (e) => {
    setSelectedIds(e.target.checked ? items.map((item) => item.id) : []);
  };

  const handleSelectOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleExport = async (format) => {
    try {
      let response;
      let filename;
      let contentType;
      const stamp = new Date().toISOString().slice(0, 10);

      switch (format) {
        case 'excel':
          response = await cityAPI.exportExcel();
          filename = `Cities_${stamp}.xlsx`;
          contentType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
          break;
        case 'csv':
          response = await cityAPI.exportCsv();
          filename = `Cities_${stamp}.csv`;
          contentType = 'text/csv;charset=utf-8';
          break;
        case 'pdf':
          response = await cityAPI.exportPdf();
          filename = `Cities_${stamp}.pdf`;
          contentType = 'application/pdf';
          break;
        default:
          return;
      }

      saveAs(new Blob([response.data], { type: contentType }), filename);
      setSuccessMessage(t(`${NS}.messages.exportSuccess`, { format: format.toUpperCase() }));
      setShowSuccessModal(true);
    } catch (err) {
      console.error('Export error:', err);
      setErrorMessage(t(`${NS}.messages.exportError`, { format: format.toUpperCase() }));
      setShowErrorModal(true);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await cityAPI.downloadTemplate();
      saveAs(response.data, `CitiesTemplate_${new Date().toISOString().slice(0, 10)}.xlsx`);
      setSuccessMessage(t('common.templateDownloadSuccess'));
      setShowSuccessModal(true);
    } catch (err) {
      console.error('Download template error:', err);
      setErrorMessage(t('common.templateDownloadError'));
      setShowErrorModal(true);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      setErrorMessage(t('common.importFileType'));
      setShowErrorModal(true);
      e.target.value = '';
      return;
    }

    try {
      const response = await cityAPI.uploadExcel(file);
      setSuccessMessage(response.data.message || t('common.importSuccess'));
      setShowSuccessModal(true);
      loadItems();
    } catch (err) {
      console.error('Import error:', err);
      setErrorMessage(err.response?.data?.message || t('common.importError'));
      setShowErrorModal(true);
    } finally {
      e.target.value = '';
    }
  };

  const toolbarActions = (
    <>
      <ExportMenu onExport={handleExport} />
      <button
        type="button"
        className="ad-icon-btn"
        title={t('common.import')}
        onClick={() => fileInputRef.current?.click()}
      >
        <i className="fas fa-file-import"></i>
      </button>
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls"
        style={{ display: 'none' }}
        onChange={handleFileUpload}
      />
      <button
        type="button"
        className="ad-icon-btn"
        title={t('common.downloadTemplate')}
        onClick={handleDownloadTemplate}
      >
        <i className="fas fa-download"></i>
      </button>
      <button
        type="button"
        className="ad-icon-btn ad-icon-btn-danger"
        title={t('common.bulkDelete')}
        onClick={handleBulkDelete}
        disabled={selectedIds.length === 0}
      >
        <i className="fas fa-trash"></i>
      </button>
    </>
  );

  return (
    <AdminPage title={t(`${NS}.title`)}>
      <PageToolbar
        onAdd={actions.openCreate}
        addLabel={t(`${NS}.addNew`)}
        searchValue={searchTerm}
        searchPlaceholder={t(`${NS}.searchPlaceholder`)}
        onSearchChange={setSearchTerm}
        onSearchSubmit={handleSearch}
        selectAll={{
          checked: selectedIds.length === items.length && items.length > 0,
          onChange: handleSelectAll,
          count: selectedIds.length,
        }}
        actions={toolbarActions}
      />

      <CardGrid
        loading={loading}
        isEmpty={items.length === 0}
        emptyTitle={t(`${NS}.messages.noData`)}
        emptyText={t('common.noResultsHint')}
        skeletonCount={PAGE_SIZE}
      >
        {items.map((item) => (
          <EntityCard
            key={item.id}
            title={item.cityNameAr}
            subtitle={item.cityNameEng}
            code={item.cityCode}
            selected={selectedIds.includes(item.id)}
            onSelect={() => handleSelectOne(item.id)}
            fields={[
              { label: t(`${NS}.columns.area`), value: item.area?.areaNameEng },
              { label: t(`${NS}.columns.country`), value: item.area?.country?.nameEng },
            ]}
            badges={[
              {
                label: item.isActive ? t('common.active') : t('common.inactive'),
                tone: item.isActive ? 'success' : 'danger',
              },
            ]}
            onView={() => actions.openDetails(item)}
            onEdit={() => actions.openEdit(item)}
            onDelete={() => actions.openDelete(item)}
          />
        ))}
      </CardGrid>

      {!loading && items.length > 0 && (
        <Pagination
          page={page}
          totalPages={totalPages}
          totalCount={totalCount}
          pageSize={PAGE_SIZE}
          onChange={setPage}
        />
      )}

      {actions.modals}

      <ErrorModal
        show={showErrorModal}
        message={errorMessage}
        onClose={() => setShowErrorModal(false)}
      />
      <SuccessModal
        show={showSuccessModal}
        message={successMessage}
        onClose={() => setShowSuccessModal(false)}
      />
      <ConfirmModal
        show={showConfirmModal}
        title={confirmData?.title}
        message={confirmData?.message}
        confirmText={confirmData?.confirmText}
        cancelText={confirmData?.cancelText}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </AdminPage>
  );
};

export default CityList;
